import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaBookOpen,
  FaChevronDown,
  FaEnvelope,
  FaFlask,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegFileAlt,
  FaSearch,
  FaTimes,
  FaUniversity,
  FaUsers,
} from "react-icons/fa";
import styles from "./style.module.css";
import LOGO from "../../Assets/logo.png";
import language from "../../localization";
import { AuthContext } from "../../authContext";
import useStore from "../../store/store";
import SearchDialog from "../../Components/Search/SearchDialog";
import institutionalRoutes from "../../config/institutionalRoutes.json";
import { localizedRoutePath } from "../../config/siteConfig";
import useMasterPrograms from "../../hooks/useMasterPrograms";
import ContactAddress from "../../Components/ContactAddress";

const text = (key, fallback = "") => language[key] || fallback;
const normalizePath = (path = "") => (path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path);

const isPathActive = (targetPath, currentPath) => {
  if (!targetPath) return false;
  const target = normalizePath(targetPath);
  const current = normalizePath(currentPath);
  if (target === "/") return current === "/";
  return current === target || current.startsWith(`${target}/`);
};

function Navbar() {
  const [globalState] = useStore();
  const { contactinfos } = globalState;
  const contactinfo = Array.isArray(contactinfos) ? contactinfos[0] : null;
  const { setLanguage, languageCode } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const lang = language.normalizeLanguage(languageCode || language.getLanguage());
  const direction = language.getLanguageDirection(lang);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [desktopGroup, setDesktopGroup] = useState(null);
  const masterPrograms = useMasterPrograms("", { pageSize: 8 });

  const onlineRegistrationLabel = text("online_registration", text("online_admission", language.online_admission));
  const kankorResultsLabel = text("kankor_results", text("result", language.result));

  const languageOptions = useMemo(
    () => [
      { code: "ps", label: "\u067e\u069a\u062a\u0648", short: "پښتو" },
      { code: "fa", label: "\u062f\u0631\u06cc", short: "دری" },
      { code: "en", label: "English", short: "English" },
    ],
    []
  );

  const quickTopLinks = useMemo(
    () => {
      // react-localization mutates a shared object; lang invalidates these derived labels.
      void lang;
      return [
      { label: text("news", language.news), to: "/news" },
      { label: onlineRegistrationLabel, to: "/online-registration" },
      { label: kankorResultsLabel, to: "/kankor/results" },
    ];
    },
    [kankorResultsLabel, onlineRegistrationLabel, lang]
  );

  const navGroups = useMemo(
    () => {
      // react-localization mutates a shared object; lang invalidates these derived labels.
      void lang;
      return [
      {
        key: "about",
        label: text("about", language.about),
        icon: <FaUniversity />,
        columns: [
          {
            heading: text("about", language.about),
            links: [
              { label: language.institution_about, to: "/about/who_we_are" },
              { label: language.achievements, to: "/about/achievements" },
              { label: language.institution_chancellor, to: "/about/chancellor_message" },
              { label: text("councils_and_committees", language.councils_and_committees), to: "/about/councils_and_committees" },
            ],
          },
          {
            heading: text("home_news_eyebrow", language.home_news_eyebrow),
            links: [
              { label: text("news", language.news), to: "/news" },
              { label: text("contact", language.contact), to: "/contact" },
              { label: text("your_opinion", language.your_opinion), to: "/about/your_opinion" },
            ],
          },
        ],
      },
      {
        key: "academics",
        label: text("academics", language.academics),
        icon: <FaBookOpen />,
        columns: [
          {
            heading: text("faculties", language.faculties),
            links: [
              { label: text("a_bcs_computer_science", language.a_bcs_computer_science), to: "/faculty/cs" },
              { label: text("a_bba_economics", language.a_bba_economics), to: "/faculty/eco" },
              { label: text("academic_programs", language.academic_programs), to: "/about/academic_programs" },
              { label: text("academic_calendar", language.academic_calendar), to: "/about/academic_calendar" },
            ],
          },
          {
            heading: language.master_programs,
            links: [
              ...(masterPrograms.data || []).map((program) => ({ label: program.code, to: `/academics/masters/${program.slug}` })),
              { label: language.master_all, to: "/academics/masters" },
            ],
          },
          {
            heading: text("a_pdc", language.a_pdc),
            links: [
              { label: text("board", language.board), to: "/pdc/a_pdc_b" },
              { label: text("training_about_pdc", language.training_about_pdc), to: "/academic/pdc/training_about_pdc" },
              { label: text("seminars_courses_workshop", language.seminars_courses_workshop), to: "/pdc/seminars_courses_workshop" },
              { label: text("aggrements", language.aggrements), to: "/pdc/aggrements" },
            ],
          },
        ],
      },
      {
        key: "admissions",
        label: text("home_admission_eyebrow", language.home_admission_eyebrow),
        icon: <FaRegFileAlt />,
        featuredAction: { label: text("home_apply_now", language.home_apply_now), to: "/online-registration" },
        columns: [
          {
            heading: text("online_admission", language.online_admission),
            links: [
              { label: onlineRegistrationLabel, to: "/online-registration" },
              { label: language.institution_fees, to: "/admissions/tuition-fees" },
              { label: text("offline_enrollment_requirement", language.offline_enrollment_requirement), to: "/kankor/offline_enrollment_requirement" },
              { label: text("enrollment_guidance", language.enrollment_guidance), to: "/kankor/enrollment_guidance" },
            ],
          },
          {
            heading: text("kankor", language.kankor),
            links: [
              { label: kankorResultsLabel, to: "/kankor/results" },
              { label: text("kankor_registration_date", language.kankor_registration_date), to: "/kankor/kankor_registration_date" },
              { label: text("kankor_guide", language.kankor_guide), to: "/kankor/kankor_guide" },
            ],
          },
          {
            heading: text("scholarships_financing", language.scholarships_financing),
            links: [
              { label: text("eligibility_criteria", language.eligibility_criteria), to: "/students/eligibility_criteria" },
              { label: text("scholarships_financing", language.scholarships_financing), to: "/students/scholarships_financing" },
            ],
          },
        ],
      },
      {
        key: "research",
        label: text("research_quality", language.research_quality),
        icon: <FaFlask />,
        columns: [
          {
            heading: text("research", language.research),
            links: [
              { label: text("activities_resources", language.activities_resources), to: "/research/activities_resources" },
              { label: text("research_trainings", language.research_trainings), to: "/research/research_trainings" },
              { label: text("research_papers_and_publication", language.research_papers_and_publication), to: "/research/research_papers_and_publication" },
              { label: text("publishedresearches", language.publishedresearches), to: "/research/published_researche" },
            ],
          },
          {
            heading: text("quality", language.quality),
            links: [
              { label: text("quality_accreditation", language.quality_accreditation), to: "/quality/quality_accreditation" },
              { label: text("manual_policies", language.manual_policies), to: "/research/manual_policies" },
              { label: text("capacity_building", language.capacity_building), to: "/research/capacity_building" },
            ],
          },
          {
            heading: text("aggrements", language.aggrements),
            links: [
              { label: text("aggrements", language.aggrements), to: "/research/aggrements" },
            ],
          },
        ],
      },
      {
        key: "students",
        label: text("student_services", language.student_services),
        icon: <FaUsers />,
        columns: [
          {
            heading: text("student", language.student),
            links: [
              { label: text("students_engagement", language.students_engagement), to: "/students/students_engagement" },
              { label: text("students_experience", language.students_experience), to: "/students/students_experience" },
              { label: text("tours_of_student", language.tours_of_student), to: "/students/tours_of_student" },
              { label: text("achievements_progress", language.achievements_progress), to: "/library/achievements_progress" },
            ],
          },
          {
            heading: text("support_services", language.support_services),
            links: [
              { label: text("counseling_center", language.counseling_center), to: "/students/counseling_center" },
              { label: text("alumni_support_services", language.alumni_support_services), to: "/student/alumni_support_services", activePaths: ["/students/alumni_support_services"] },
            ],
          },
          {
            heading: text("library", language.library),
            links: [
              { label: text("online_library", language.online_library), to: "/library/online_library" },
              { label: text("offline_library", language.offline_library), to: "/library/offline_library" },
            ],
          },
        ],
      },
      {
        key: "institutions",
        label: text("institutions", language.institutions),
        icon: <FaGraduationCap />,
        columns: [
          {
            heading: text("institutions", language.institutions),
            links: [
              { label: text("saba_economical_board", language.saba_economical_board), to: "/institutions/seb" },
              { label: text("saba_foundation", language.saba_foundation), to: "/foundation" },
              { label: text("activity", language.activity), to: "/institutions/seb/activity" },
            ],
          },
          {
            heading: text("contact", language.contact),
            links: [
              { label: text("contact", language.contact), to: "/contact" },
              { label: text("your_opinion", language.your_opinion), to: "/about/your_opinion" },
            ],
          },
        ],
      },
    ];
    },
    [kankorResultsLabel, onlineRegistrationLabel, lang, masterPrograms.data]
  );

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
    setDesktopGroup(null);
  }, [location.pathname]);

  const changeLanguage = (code) => {
    setLanguage(code);
    setMobileOpen(false);
    setDesktopGroup(null);
    setOpenGroup(null);
    if (institutionalRoutes[location.pathname]) {
      const query = new URLSearchParams(location.search);
      query.set("lang", code);
      navigate({ pathname: location.pathname, search: query.toString(), hash: location.hash }, { replace: true });
    }
  };

  const openSearch = () => {
    setMobileOpen(false);
    setIsSearchOpen(true);
  };

  const itemIsActive = (item) =>
    [item?.to, ...(item?.activePaths || [])]
      .filter(Boolean)
      .some((path) => isPathActive(path, location.pathname));

  const renderLink = (item, className = styles.megaLink, onClick) => (
    <NavLink
      key={`${item.to}-${item.label}`}
      to={institutionalRoutes[item.to] || item.to.startsWith("/academics/masters") || item.to === "/library/online_library" ? localizedRoutePath(item.to, lang) : item.to}
      onClick={onClick}
      className={({ isActive }) => [className, isActive || itemIsActive(item) ? styles.active : ""].filter(Boolean).join(" ")}
    >
      <span className={styles.linkMark} aria-hidden="true" />
      <span>{item.label}</span>
    </NavLink>
  );

  const groupIsActive = (group) =>
    [
      ...(group.columns || []).flatMap((column) => column.links || []),
      group.featuredAction,
    ]
      .filter(Boolean)
      .some((item) => itemIsActive(item));

  return (
    <>
      <header className={styles.siteHeader} dir={direction}>
        <div className={styles.topStrip}>
          <div className={[styles.topInner, "w-controller"].join(" ")}>
            <div className={styles.contactList}>
              {contactinfo?.phone && (
                <a href={`tel:${contactinfo.phone}`} className={styles.contactItem}>
                  <FaPhoneAlt aria-hidden="true" />
                  <span>{contactinfo.phone}</span>
                </a>
              )}
              {contactinfo?.email && (
                <a href={`mailto:${contactinfo.email}`} className={styles.contactItem}>
                  <FaEnvelope aria-hidden="true" />
                  <span>{contactinfo.email}</span>
                </a>
              )}
              {contactinfo && (
                <NavLink to="/contact" className={styles.contactItem}>
                  <FaMapMarkerAlt aria-hidden="true" />
                  <ContactAddress html={language.getLocalizedField(contactinfo, "address", lang) || text("contact", language.contact)} compact />
                </NavLink>
              )}
            </div>
            <div className={styles.utilityArea}>
              <div className={styles.quickStripLinks}>
                {quickTopLinks.map((item) => (
                  <NavLink key={item.to} to={item.to} className={styles.stripLink}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
              <div className={styles.languageSwitch} aria-label={language.language_selector}>
                {languageOptions.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    className={[styles.langButton, lang === option.code ? styles.active : ""].filter(Boolean).join(" ")}
                    onClick={() => changeLanguage(option.code)}
                  >
                    {option.short}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <nav className={styles.mainNav} aria-label={language.primary_navigation}>
          <div className={[styles.navInner, "w-controller"].join(" ")}>
            <NavLink to="/" className={styles.brand} aria-label={language.saba_university_home}>
              <span className={styles.logoFrame}>
                <img src={LOGO} alt={language.saba_university} />
              </span>
              <span className={styles.brandText}>
                <strong>{text("saba_university", language.saba_university)}</strong>
                {/* <small>{text("home_hero_eyebrow", language.home_hero_eyebrow)}</small> */}
              </span>
            </NavLink>
            <div className={styles.desktopNav}>
              <NavLink
                to="/"
                className={({ isActive }) => [styles.navLink, isActive ? styles.active : ""].filter(Boolean).join(" ")}
              >
                {text("home", language.home)}
              </NavLink>
              {navGroups.map((group) => {
                const active = groupIsActive(group);
                return (
                  <div key={group.key} className={[styles.navGroup, active ? styles.active : ""].filter(Boolean).join(" ")}
                    data-open={desktopGroup === group.key ? "true" : undefined}
                    onPointerEnter={() => setDesktopGroup(group.key)}
                    onPointerLeave={(event) => { if (!event.currentTarget.contains(document.activeElement)) setDesktopGroup(null); }}
                    onFocusCapture={() => setDesktopGroup(group.key)}
                    onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDesktopGroup(null); }}
                    onKeyDown={(event) => { if (event.key === "Escape") { event.currentTarget.querySelector("button").focus(); setDesktopGroup(null); } }}>
                    <button className={styles.navButton} type="button" aria-expanded={desktopGroup === group.key}
                      aria-controls={`desktop-nav-${group.key}`} onClick={() => setDesktopGroup(group.key)}>
                      <span className={styles.navIcon} aria-hidden="true">{group.icon}</span>
                      <span>{group.label}</span>
                      <FaChevronDown className={styles.chevron} aria-hidden="true" />
                    </button>
                    <div className={styles.megaMenu} id={`desktop-nav-${group.key}`}>
                      <div className={styles.megaGrid}>
                        {group.columns.map((column) => (
                          <section className={styles.megaSection} key={`${group.key}-${column.heading}`}>
                            <h3>{column.heading}</h3>
                            <div>{column.links.map((item) => renderLink(item))}</div>
                          </section>
                        ))}
                      </div>
                      {group.featuredAction && (
                        <NavLink to={group.featuredAction.to} className={styles.featuredAction}>
                          <span>{group.featuredAction.label}</span>
                          <FaRegFileAlt aria-hidden="true" />
                        </NavLink>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.navActions}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={openSearch}
                aria-label={text("search_open", language.search_open)}
                title={text("search_open", language.search_open)}
              >
                <FaSearch aria-hidden="true" />
              </button>
              <NavLink to={localizedRoutePath("/library/online_library", lang)}
                className={({ isActive }) => [styles.libraryLink, isActive ? styles.active : ""].filter(Boolean).join(" ")}>
                <FaBookOpen aria-hidden="true" />
                <span>{language.online_library}</span>
              </NavLink>
              <NavLink to="/online-registration" className={styles.applyLink}>
                {text("home_apply_now", language.home_apply_now)}
              </NavLink>
              <button
                type="button"
                className={styles.mobileToggle}
                onClick={() => setMobileOpen((value) => !value)}
                aria-expanded={mobileOpen}
                aria-label={mobileOpen ? language.close_menu : language.open_menu}
              >
                {mobileOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
              </button>
            </div>
          </div>
          <div className={[styles.mobilePanel, mobileOpen ? styles.open : ""].filter(Boolean).join(" ")}>
            <div className={styles.mobileQuickLinks}>
              {quickTopLinks.map((item) => renderLink(item, styles.mobileQuickLink, () => setMobileOpen(false)))}
              {renderLink({ label: language.online_library, to: "/library/online_library" }, styles.mobileQuickLink, () => setMobileOpen(false))}
              <button type="button" className={styles.mobileQuickLink} onClick={openSearch}>
                <span className={styles.linkMark} aria-hidden="true" />
                <span>{text("search", language.search)}</span>
              </button>
            </div>
            <NavLink to="/" className={({ isActive }) => [styles.mobileHome, isActive ? styles.active : ""].filter(Boolean).join(" ")}>
              <span className={styles.linkMark} aria-hidden="true" />
              <span>{text("home", language.home)}</span>
            </NavLink>
            {navGroups.map((group) => {
              const expanded = openGroup === group.key;
              return (
                <section className={[styles.mobileGroup, expanded ? styles.expanded : ""].filter(Boolean).join(" ")} key={`mobile-${group.key}`}>
                  <button type="button" className={styles.mobileGroupButton} aria-expanded={expanded} aria-controls={`mobile-nav-${group.key}`} onClick={() => setOpenGroup(expanded ? null : group.key)}>
                    <span className={styles.mobileGroupLabel}>
                      {group.icon}
                      {group.label}
                    </span>
                    <FaChevronDown aria-hidden="true" />
                  </button>
                  <div className={styles.mobileGroupContent} id={`mobile-nav-${group.key}`}>
                    {group.columns.map((column) => (
                      <div className={styles.mobileSection} key={`mobile-${group.key}-${column.heading}`}>
                        <h3>{column.heading}</h3>
                        {column.links.map((item) => renderLink(item, styles.mobileNestedLink, () => setMobileOpen(false)))}
                      </div>
                    ))}
                    {group.featuredAction && renderLink(group.featuredAction, styles.mobileNestedLink, () => setMobileOpen(false))}
                  </div>
                </section>
              );
            })}

            <div className={styles.mobileLanguageSwitch} aria-label={language.language_selector}>
              {languageOptions.map((option) => (
                <button
                  key={`mobile-${option.code}`}
                  type="button"
                  className={[styles.langButton, lang === option.code ? styles.active : ""].filter(Boolean).join(" ")}
                  onClick={() => changeLanguage(option.code)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </nav>
      </header>
      <SearchDialog open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
}

export default Navbar;
